import React from "react";
import { Mail, MapPin, Phone, Instagram, MessageCircle } from "lucide-react";
import { COMPANY } from "../lib/siteData";
import EnquiryForm from "../components/EnquiryForm";

const TikTokIcon = ({ size = 18 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
);

export default function ContactPage() {
    return (
        <div data-testid="page-contact">
            <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-14 items-end">
                    <div className="md:col-span-8">
                        <div className="eyebrow mb-6">Contact</div>
                        <h1 className="font-display text-5xl md:text-7xl leading-[1] text-hhc-ink">
                            Let's begin your home.
                        </h1>
                    </div>
                    <div className="md:col-span-4">
                        <p className="text-hhc-muted text-[17px] leading-[1.75]">
                            We reply within 48 hours on business days. For
                            faster response, message us on WhatsApp.
                        </p>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <aside className="md:col-span-4 space-y-8">
                        <InfoCard
                            icon={<MapPin size={18} />}
                            label="Studio"
                            testId="contact-address"
                        >
                            {COMPANY.address}
                        </InfoCard>
                        <InfoCard
                            icon={<Phone size={18} />}
                            label="Phone / WhatsApp"
                            testId="contact-phone"
                        >
                            <a
                                href={`tel:+${COMPANY.phoneRaw}`}
                                className="hover:text-hhc-clay transition-colors"
                            >
                                {COMPANY.phone}
                            </a>
                        </InfoCard>
                        <InfoCard
                            icon={<Mail size={18} />}
                            label="Email"
                            testId="contact-email"
                        >
                            <a
                                href={`mailto:${COMPANY.email}`}
                                className="hover:text-hhc-clay transition-colors break-all"
                            >
                                {COMPANY.email}
                            </a>
                        </InfoCard>

                        <div className="rounded-2xl border border-hhc-line p-6 bg-hhc-cream/50">
                            <div className="text-[11px] tracking-[0.28em] uppercase text-hhc-clay mb-4">
                                Follow the studio
                            </div>
                            <div className="flex items-center gap-3">
                                <SocialChip
                                    href={COMPANY.whatsapp}
                                    label="WhatsApp"
                                    icon={<MessageCircle size={16} />}
                                    testId="contact-social-whatsapp"
                                    accent
                                />
                                <SocialChip
                                    href={COMPANY.instagram}
                                    label="Instagram"
                                    icon={<Instagram size={16} />}
                                    testId="contact-social-instagram"
                                />
                                <SocialChip
                                    href={COMPANY.tiktok}
                                    label="TikTok"
                                    icon={<TikTokIcon size={16} />}
                                    testId="contact-social-tiktok"
                                />
                            </div>
                        </div>

                        <div className="rounded-2xl overflow-hidden border border-hhc-line aspect-[4/3]">
                            <iframe
                                title="HEYHOMECAN Studio Location"
                                data-testid="contact-map"
                                width="100%"
                                height="100%"
                                loading="lazy"
                                src="https://www.google.com/maps?q=Taman+Danau+Kota,+53300+Setapak,+Kuala+Lumpur&output=embed"
                                style={{ border: 0 }}
                            />
                        </div>
                    </aside>

                    <div className="md:col-span-8">
                        <div className="rounded-2xl bg-white border border-hhc-line p-8 md:p-12">
                            <div className="mb-8">
                                <div className="eyebrow mb-3">Enquiry form</div>
                                <h2 className="font-display text-3xl md:text-4xl text-hhc-ink">
                                    Tell us about your space.
                                </h2>
                            </div>
                            <EnquiryForm />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

const InfoCard = ({ icon, label, children, testId }) => (
    <div
        className="border-b border-hhc-line pb-6"
        data-testid={testId}
    >
        <div className="flex items-center gap-3 text-hhc-clay">
            {icon}
            <div className="text-[11px] tracking-[0.28em] uppercase font-semibold">
                {label}
            </div>
        </div>
        <div className="mt-3 text-[15px] text-hhc-ink leading-relaxed">
            {children}
        </div>
    </div>
);

const SocialChip = ({ href, label, icon, testId, accent }) => (
    <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        data-testid={testId}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] tracking-[0.16em] uppercase font-medium transition-colors ${
            accent
                ? "bg-hhc-ink text-hhc-ivory hover:bg-hhc-clay"
                : "border border-hhc-line text-hhc-ink hover:border-hhc-ink hover:bg-hhc-ink hover:text-hhc-ivory"
        }`}
    >
        {icon}
        {label}
    </a>
);
